import React, { useState, useEffect } from 'react';
import { 
    Card, 
    Tabs, 
    Switch, 
    Select, 
    Input, 
    Button, 
    Form, 
    message, 
    Divider, 
    Row, 
    Col, 
    Typography, 
    Slider, 
    Space,
    Alert,
    Spin
} from 'antd';
import { 
    SettingOutlined, 
    BellOutlined, 
    UserOutlined, 
    GlobalOutlined,
    SoundOutlined,
    MailOutlined,
    MobileOutlined,
    DesktopOutlined,
    RiseOutlined
} from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthContext';
import api from '../../../services/api';
import UserAnalytics from '../Analytics/UserAnalytics';
import PWAFeatures from '../../UI/PWAFeatures';

const { Title, Text } = Typography;
const { Option } = Select;

const Settings = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [preferences, setPreferences] = useState({});
    const [notificationPreferences, setNotificationPreferences] = useState({});
    const [form] = Form.useForm();

    useEffect(() => {
        fetchPreferences();
    }, []);

    const fetchPreferences = async () => {
        try {
            setLoading(true);
            const response = await api.getPreferences();
            if (response.success) {
                setPreferences(response.data.preferences || {});
                setNotificationPreferences(response.data.notification_preferences || {});
                form.setFieldsValue({
                    ...response.data.preferences,
                    ...response.data.notification_preferences
                });
            }
        } catch (error) {
            console.error('Failed to fetch preferences:', error);
            message.error('Failed to load preferences');
        } finally {
            setLoading(false);
        }
    };

    const handlePreferenceChange = async (changedValues) => {
        try {
            setSaving(true);
            const response = await api.updatePreferences(changedValues);
            if (response.success) {
                setPreferences(prev => ({ ...prev, ...changedValues }));
                message.success('Preferences updated successfully');
            } else {
                message.error(response.message || 'Failed to update preferences');
            }
        } catch (error) {
            console.error('Failed to update preferences:', error);
            message.error('Failed to update preferences');
        } finally {
            setSaving(false);
        }
    };

    const handleNotificationPreferenceChange = async (changedValues) => {
        try {
            setSaving(true);
            const response = await api.updateNotificationPreferences(changedValues);
            if (response.success) {
                setNotificationPreferences(prev => ({ ...prev, ...changedValues }));
                message.success('Notification preferences updated successfully');
            } else {
                message.error(response.message || 'Failed to update notification preferences');
            }
        } catch (error) {
            console.error('Failed to update notification preferences:', error);
            message.error('Failed to update notification preferences');
        } finally {
            setSaving(false);
        }
    };

    const resetPreferences = async (type = 'all') => {
        try {
            setSaving(true);
            const response = await api.resetPreferences(type);
            if (response.success) {
                await fetchPreferences();
                message.success('Preferences reset to default successfully');
            } else {
                message.error(response.message || 'Failed to reset preferences');
            }
        } catch (error) {
            console.error('Failed to reset preferences:', error);
            message.error('Failed to reset preferences');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-96">
                <Spin size="large" />
            </div>
        );
    }

    const tabItems = [
        {
            key: 'general',
            label: (
                <span>
                    <SettingOutlined />
                    General
                </span>
            ),
            children: (
                <Form
                    form={form}
                    layout="vertical"
                    onValuesChange={handlePreferenceChange}
                    initialValues={preferences}
                >
                    <Row gutter={[24, 24]}>
                        <Col xs={24} md={12}>
                            <Card title="Appearance" size="small">
                                <Form.Item label="Theme" name="theme">
                                    <Select>
                                        <Option value="light">Light</Option>
                                        <Option value="dark">Dark</Option>
                                        <Option value="auto">Auto (System)</Option>
                                    </Select>
                                </Form.Item>
                                
                                <Form.Item label="Language" name="language">
                                    <Select>
                                        <Option value="en">English</Option>
                                        <Option value="hi">Hindi</Option>
                                        <Option value="ta">Tamil</Option>
                                        <Option value="te">Telugu</Option>
                                    </Select>
                                </Form.Item>
                            </Card>
                        </Col>

                        <Col xs={24} md={12}>
                            <Card title="Formatting" size="small">
                                <Form.Item label="Date Format" name="date_format">
                                    <Select>
                                        <Option value="Y-m-d">YYYY-MM-DD</Option>
                                        <Option value="d-m-Y">DD-MM-YYYY</Option>
                                        <Option value="m/d/Y">MM/DD/YYYY</Option>
                                        <Option value="d/m/Y">DD/MM/YYYY</Option>
                                    </Select>
                                </Form.Item>
                                
                                <Form.Item label="Time Format" name="time_format">
                                    <Select>
                                        <Option value="12h">12 Hour (AM/PM)</Option>
                                        <Option value="24h">24 Hour</Option>
                                    </Select>
                                </Form.Item>
                                
                                <Form.Item label="Currency" name="currency">
                                    <Select>
                                        <Option value="INR">₹ INR</Option>
                                        <Option value="USD">$ USD</Option>
                                        <Option value="EUR">€ EUR</Option>
                                    </Select>
                                </Form.Item>
                            </Card>
                        </Col>

                        <Col xs={24} md={12}>
                            <Card title="Interface" size="small">
                                <Form.Item label="Dashboard Layout" name="dashboard_layout">
                                    <Select>
                                        <Option value="grid">Grid View</Option>
                                        <Option value="list">List View</Option>
                                    </Select>
                                </Form.Item>
                                
                                <Form.Item label="Items per Page" name="items_per_page">
                                    <Select>
                                        <Option value={5}>5 items</Option>
                                        <Option value={10}>10 items</Option>
                                        <Option value={20}>20 items</Option>
                                        <Option value={50}>50 items</Option>
                                    </Select>
                                </Form.Item>
                            </Card>
                        </Col>

                        <Col xs={24} md={12}>
                            <Card title="Location" size="small">
                                <Form.Item label="Timezone" name="timezone">
                                    <Select showSearch>
                                        <Option value="UTC">UTC</Option>
                                        <Option value="Asia/Kolkata">Asia/Kolkata (IST)</Option>
                                        <Option value="America/New_York">America/New_York (EST)</Option>
                                        <Option value="Europe/London">Europe/London (GMT)</Option>
                                    </Select>
                                </Form.Item>
                            </Card>
                        </Col>
                    </Row>
                </Form>
            )
        },
        {
            key: 'notifications',
            label: (
                <span>
                    <BellOutlined />
                    Notifications
                </span>
            ),
            children: (
                <Form
                    form={form}
                    layout="vertical"
                    onValuesChange={handleNotificationPreferenceChange}
                    initialValues={notificationPreferences}
                >
                    <Row gutter={[24, 24]}>
                        <Col xs={24} md={8}>
                            <Card title={<><MailOutlined /> Email Notifications</>} size="small">
                                <Form.Item name={['email', 'enabled']} valuePropName="checked">
                                    <Switch checkedChildren="On" unCheckedChildren="Off" />
                                </Form.Item>
                                
                                <Divider />
                                
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <div className="flex justify-between items-center">
                                        <Text>Booking Updates</Text>
                                        <Form.Item name={['email', 'booking_updates']} valuePropName="checked" noStyle>
                                            <Switch size="small" />
                                        </Form.Item>
                                    </div>
                                    
                                    <div className="flex justify-between items-center">
                                        <Text>Quote Updates</Text>
                                        <Form.Item name={['email', 'quote_updates']} valuePropName="checked" noStyle>
                                            <Switch size="small" />
                                        </Form.Item>
                                    </div>
                                    
                                    <div className="flex justify-between items-center">
                                        <Text>Promotional</Text>
                                        <Form.Item name={['email', 'promotional']} valuePropName="checked" noStyle>
                                            <Switch size="small" />
                                        </Form.Item>
                                    </div>
                                    
                                    <div className="flex justify-between items-center">
                                        <Text>System Notifications</Text>
                                        <Form.Item name={['email', 'system_notifications']} valuePropName="checked" noStyle>
                                            <Switch size="small" />
                                        </Form.Item>
                                    </div>
                                </Space>
                            </Card>
                        </Col>

                        <Col xs={24} md={8}>
                            <Card title={<><MobileOutlined /> Push Notifications</>} size="small">
                                <Form.Item name={['push', 'enabled']} valuePropName="checked">
                                    <Switch checkedChildren="On" unCheckedChildren="Off" />
                                </Form.Item>
                                
                                <Divider />
                                
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <div className="flex justify-between items-center">
                                        <Text>Booking Updates</Text>
                                        <Form.Item name={['push', 'booking_updates']} valuePropName="checked" noStyle>
                                            <Switch size="small" />
                                        </Form.Item>
                                    </div>
                                    
                                    <div className="flex justify-between items-center">
                                        <Text>Quote Updates</Text>
                                        <Form.Item name={['push', 'quote_updates']} valuePropName="checked" noStyle>
                                            <Switch size="small" />
                                        </Form.Item>
                                    </div>
                                    
                                    <div className="flex justify-between items-center">
                                        <Text>Promotional</Text>
                                        <Form.Item name={['push', 'promotional']} valuePropName="checked" noStyle>
                                            <Switch size="small" />
                                        </Form.Item>
                                    </div>
                                    
                                    <div className="flex justify-between items-center">
                                        <Text>System Notifications</Text>
                                        <Form.Item name={['push', 'system_notifications']} valuePropName="checked" noStyle>
                                            <Switch size="small" />
                                        </Form.Item>
                                    </div>
                                </Space>
                            </Card>
                        </Col>

                        <Col xs={24} md={8}>
                            <Card title={<><DesktopOutlined /> In-App Notifications</>} size="small">
                                <Form.Item name={['in_app', 'enabled']} valuePropName="checked">
                                    <Switch checkedChildren="On" unCheckedChildren="Off" />
                                </Form.Item>
                                
                                <Divider />
                                
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <div className="flex justify-between items-center">
                                        <Text>Booking Updates</Text>
                                        <Form.Item name={['in_app', 'booking_updates']} valuePropName="checked" noStyle>
                                            <Switch size="small" />
                                        </Form.Item>
                                    </div>
                                    
                                    <div className="flex justify-between items-center">
                                        <Text>Quote Updates</Text>
                                        <Form.Item name={['in_app', 'quote_updates']} valuePropName="checked" noStyle>
                                            <Switch size="small" />
                                        </Form.Item>
                                    </div>
                                    
                                    <div className="flex justify-between items-center">
                                        <Text>Promotional</Text>
                                        <Form.Item name={['in_app', 'promotional']} valuePropName="checked" noStyle>
                                            <Switch size="small" />
                                        </Form.Item>
                                    </div>
                                    
                                    <div className="flex justify-between items-center">
                                        <Text>System Notifications</Text>
                                        <Form.Item name={['in_app', 'system_notifications']} valuePropName="checked" noStyle>
                                            <Switch size="small" />
                                        </Form.Item>
                                    </div>
                                </Space>
                            </Card>
                        </Col>

                        <Col xs={24}>
                            <Card title={<><SoundOutlined /> Sound Settings</>} size="small">
                                <Row gutter={[24, 24]}>
                                    <Col xs={24} md={12}>
                                        <Form.Item name={['sound', 'enabled']} valuePropName="checked">
                                            <Switch checkedChildren="On" unCheckedChildren="Off" />
                                        </Form.Item>
                                        
                                        <Form.Item label="Volume" name={['sound', 'volume']}>
                                            <Slider
                                                min={0}
                                                max={1}
                                                step={0.1}
                                                marks={{
                                                    0: '0%',
                                                    0.5: '50%',
                                                    1: '100%'
                                                }}
                                            />
                                        </Form.Item>
                                    </Col>
                                    
                                    <Col xs={24} md={12}>
                                        <Form.Item label="Ringtone" name={['sound', 'ringtone']}>
                                            <Select>
                                                <Option value="default">Default</Option>
                                                <Option value="chime">Chime</Option>
                                                <Option value="bell">Bell</Option>
                                                <Option value="notification">Notification</Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>
                        </Col>
                    </Row>
                </Form>
            )
        },
        {
            key: 'analytics',
            label: (
                <span>
                    <RiseOutlined />
                    Analytics
                </span>
            ),
            children: <UserAnalytics />
        },
        {
            key: 'pwa',
            label: (
                <span>
                    <MobileOutlined />
                    PWA Features
                </span>
            ),
            children: <PWAFeatures />
        }
    ];

    return (
        <div className="px-4 sm:px-6 py-6">
            <div className="max-w-7xl mx-auto">
                <div className="mb-6">
                    <Title level={2}>
                        <SettingOutlined className="mr-2" />
                        Settings
                    </Title>
                    <Text type="secondary">
                        Manage your preferences and notification settings
                    </Text>
                </div>

                <Alert
                    message="Settings are automatically saved"
                    description="Your preferences are saved automatically when you make changes."
                    type="info"
                    showIcon
                    className="mb-6"
                />

                <Card>
                    <Tabs 
                        defaultActiveKey="general" 
                        items={tabItems}
                        size="large"
                    />
                </Card>

                <div className="mt-6 flex justify-end space-x-4">
                    <Button 
                        onClick={() => resetPreferences('preferences')}
                        disabled={saving}
                    >
                        Reset General Settings
                    </Button>
                    <Button 
                        onClick={() => resetPreferences('notifications')}
                        disabled={saving}
                    >
                        Reset Notification Settings
                    </Button>
                    <Button 
                        type="primary" 
                        danger
                        onClick={() => resetPreferences('all')}
                        disabled={saving}
                    >
                        Reset All Settings
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default Settings;
